<script setup>
/* global axios */
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';

// Card-4.9 (2026-04-29) — antes importava axios direto da lib (sem
// auth headers), causando 401 em /pipeline/activity_sequences. O
// global window.axios é o instance configurado pelo APIHelper com
// access-token + uid + client. Padrão usado no resto do dashboard
// (vide googleCalendar.js, etc).

const props = defineProps({
  cardId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits(['close', 'started']);
const { t } = useI18n();
const store = useStore();

const definitions = ref([]);
const selectedDefinitionId = ref(null);
const isLoading = ref(false);
const isStarting = ref(false);
const error = ref(null);

const fetchDefinitions = async () => {
  isLoading.value = true;
  try {
    const accountId = store.getters.getCurrentAccountId;
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId}/pipeline/activity_sequences?active=true&per_page=50`
    );
    definitions.value = data.data || [];
  } catch (err) {
    error.value = t('PIPELINE.SEQUENCES.FETCH_DEFINITIONS_ERROR');
  } finally {
    isLoading.value = false;
  }
};

const handleStart = async () => {
  if (!selectedDefinitionId.value) return;
  isStarting.value = true;
  error.value = null;
  try {
    await PipelineCardsAPI.startSequence(
      props.cardId,
      selectedDefinitionId.value
    );
    emit('started');
  } catch (err) {
    error.value =
      err.response?.data?.error || t('PIPELINE.SEQUENCES.START_ERROR');
  } finally {
    isStarting.value = false;
  }
};

const selectedDefinition = () =>
  definitions.value.find(d => d.id === selectedDefinitionId.value);

onMounted(fetchDefinitions);
</script>

<template>
  <!-- Teleport to body: the modal is mounted inside the card's right panel
  (CardAssigneePanel) which is `hidden lg:block`, so on viewports < lg the
  parent's display:none suppressed this fixed overlay. Teleporting to <body>
  detaches it from that hidden container so the modal shows at any width. -->
  <Teleport to="body">
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="emit('close')"
    >
      <div
        class="bg-white dark:bg-n-solid-1 rounded-xl shadow-xl w-full max-w-md mx-4 p-6"
      >
        <!-- Header -->
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ t('PIPELINE.SEQUENCES.MODAL_TITLE') }}
          </h3>
          <button
            class="text-n-slate-11 hover:text-n-slate-12 transition-colors"
            @click="emit('close')"
          >
            <fluent-icon icon="dismiss" size="16" />
          </button>
        </div>

        <!-- Loading -->
        <div v-if="isLoading" class="text-sm text-n-slate-11 py-4 text-center">
          {{ t('PIPELINE.SEQUENCES.LOADING') }}
        </div>

        <!-- Empty -->
        <div
          v-else-if="definitions.length === 0"
          class="text-sm text-n-slate-11 py-4 text-center"
        >
          {{ t('PIPELINE.SEQUENCES.NO_DEFINITIONS') }}
        </div>

        <!-- Definition selector -->
        <div v-else>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ t('PIPELINE.SEQUENCES.SELECT_DEFINITION') }}
          </label>
          <select
            v-model="selectedDefinitionId"
            class="w-full rounded-lg border border-n-weak bg-white dark:bg-n-solid-2 text-sm text-n-slate-12 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500/30"
          >
            <option :value="null" disabled>
              {{ t('PIPELINE.SEQUENCES.SELECT_PLACEHOLDER') }}
            </option>
            <option v-for="def in definitions" :key="def.id" :value="def.id">
              {{ def.name }} ({{ def.step_count }}
              {{ t('PIPELINE.SEQUENCES.STEPS_UNIT') }})
            </option>
          </select>

          <!-- Preview selected -->
          <div
            v-if="selectedDefinitionId"
            class="mt-3 p-3 rounded-lg bg-n-alpha-1 border border-n-weak"
          >
            <p class="text-xs font-medium text-n-slate-12 mb-1">
              {{ selectedDefinition()?.name }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ selectedDefinition()?.step_count }}
              {{ t('PIPELINE.SEQUENCES.STEPS_UNIT') }}
              <template v-if="selectedDefinition()?.total_duration_days">
                · {{ selectedDefinition().total_duration_days }}
                {{ t('PIPELINE.SEQUENCES.DAYS_TOTAL') }}
              </template>
            </p>
          </div>

          <!-- Error -->
          <p v-if="error" class="text-xs text-red-500 mt-2">{{ error }}</p>

          <!-- Actions -->
          <div class="flex items-center justify-end gap-3 mt-4">
            <button
              class="px-4 py-2 text-sm text-n-slate-11 hover:text-n-slate-12 transition-colors"
              @click="emit('close')"
            >
              {{ t('PIPELINE.SEQUENCES.CANCEL') }}
            </button>
            <button
              :disabled="!selectedDefinitionId || isStarting"
              class="px-4 py-2 text-sm font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              @click="handleStart"
            >
              {{
                isStarting
                  ? t('PIPELINE.SEQUENCES.STARTING')
                  : t('PIPELINE.SEQUENCES.START_BUTTON')
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
